import React, { useState } from 'react';
import {
  Typography,
  Stack,
  Divider,
  Alert,
  Button,
  IconButton,
  styled,
  Chip,
} from '@mui/material';
import {
  Folder as FolderIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import DocumentCard from './DocumentCard';

interface TaskDocumentAttachment {
  id: string;
  document_node_id: string;
  document_title?: string;
  document_type?: string;
  document_source?: string;
  document_url?: string;
  attachment_method: 'manual' | 'automatic' | 'suggested';
  relevance_score?: number;
  attachment_context?: string;
  attached_at: string;
}

interface TaskDocumentsProps {
  taskTitle: string;
  attachments: TaskDocumentAttachment[];
  totalCount: number;
  onDetachDocument?: (documentId: string) => void;
  onViewDocument?: (url: string) => void;
  onSearchDocuments?: () => void;
  maxVisible?: number;
}

const DocumentsHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(2),
  padding: theme.spacing(1.5),
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
}));

const DocumentsContainer = styled('div')(({ theme }) => ({
  maxHeight: '400px',
  overflowY: 'auto',
  padding: theme.spacing(1),
  backgroundColor: theme.palette.background.default,
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
}));

const TaskDocuments: React.FC<TaskDocumentsProps> = ({
  taskTitle,
  attachments,
  totalCount,
  onDetachDocument,
  onViewDocument,
  onSearchDocuments,
  maxVisible = 5,
}) => {
  const [showAll, setShowAll] = useState(false);

  if (attachments.length === 0) {
    return (
      <div style={{ padding: 16 }}>
        <DocumentsHeader>
          <FolderIcon color="action" />
          <Typography variant="h6" color="text.secondary">
            No Documents Attached
          </Typography>
        </DocumentsHeader>

        <Alert severity="info" style={{ marginTop: 16 }}>
          <Typography variant="body2" style={{ marginBottom: 8 }}>
            No documents attached to "{taskTitle}" yet.
          </Typography>
          <Typography variant="body2" style={{ fontSize: '0.875rem' }}>
            Use document search to find and attach relevant documents to this task.
          </Typography>
          {onSearchDocuments && (
            <Button
              startIcon={<SearchIcon />}
              onClick={onSearchDocuments}
              size="small"
              variant="outlined"
              color="primary"
              style={{ marginTop: 8 }}
            >
              Search Documents
            </Button>
          )}
        </Alert>
      </div>
    );
  }

  const visibleAttachments = showAll ? attachments : attachments.slice(0, maxVisible);
  const hasMore = attachments.length > maxVisible;

  // Group attachments by method
  const methodCounts = attachments.reduce((acc, attachment) => {
    acc[attachment.attachment_method] = (acc[attachment.attachment_method] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  return (
    <div style={{ padding: 16 }}>
      <DocumentsHeader>
        <FolderIcon color="primary" />
        <div style={{ flexGrow: 1 }}>
          <Typography variant="h6">
            "{taskTitle}"
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {totalCount} document{totalCount > 1 ? 's' : ''} attached
          </Typography>
        </div>

        <Stack direction="row" spacing={1}>
          {methodCounts.manual && (
            <Chip
              label={`${methodCounts.manual} Manual`}
              size="small"
              color="primary"
              variant="outlined"
            />
          )}
          {methodCounts.automatic && (
            <Chip
              label={`${methodCounts.automatic} Auto`}
              size="small"
              color="secondary"
              variant="outlined"
            />
          )}
          {methodCounts.suggested && (
            <Chip
              label={`${methodCounts.suggested} Suggested`}
              size="small"
              color="info"
              variant="outlined"
            />
          )}
        </Stack>

        {onSearchDocuments && (
          <Button
            startIcon={<SearchIcon />}
            onClick={onSearchDocuments}
            size="small"
            variant="outlined"
            color="primary"
          >
            Find More
          </Button>
        )}

        {hasMore && (
          <IconButton
            onClick={() => setShowAll(!showAll)}
            size="small"
            color="primary"
          >
            {showAll ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        )}
      </DocumentsHeader>

      <DocumentsContainer>
        <Stack spacing={2}>
          {visibleAttachments.map((attachment) => (
            <DocumentCard
              key={attachment.id}
              document={{
                title: attachment.document_title || 'Untitled Document',
                type: attachment.document_type || 'Document',
                relevance: attachment.relevance_score || 0.5,
                source: attachment.document_source || 'Unknown',
                url: attachment.document_url,
                document_node_id: attachment.document_node_id,
                attachment_reason: attachment.attachment_context ||
                  `Attached ${attachment.attachment_method}${
                    attachment.attached_at
                      ? ` on ${new Date(attachment.attached_at).toLocaleDateString()}`
                      : ''
                  }`,
              }}
              isAttached={true}
              onDetach={onDetachDocument}
              onView={onViewDocument}
              compact={visibleAttachments.length > 3}
            />
          ))}

          {hasMore && !showAll && (
            <>
              <Divider />
              <div style={{ textAlign: 'center', paddingTop: 8, paddingBottom: 8 }}>
                <Button
                  onClick={() => setShowAll(true)}
                  startIcon={<ExpandMoreIcon />}
                  color="primary"
                  variant="text"
                >
                  Show {attachments.length - maxVisible} more documents
                </Button>
              </div>
            </>
          )}
        </Stack>
      </DocumentsContainer>
    </div>
  );
};

export default TaskDocuments;
